import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useWorkspace2Actions } from 'components/WorkspaceEditor/Workspace2Store'
import {
    useCKEditorToggle,
    useContentItemViewNode,
} from 'components/WorkspaceEditor/hooks'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import {
    replaceNodeNotesByKey,
    withDebounce,
} from 'components/WorkspaceEditor/utils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NodeTypes } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useMemo } from 'react'
import {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
    utils,
} from '../../ContentEditor'
import { NoteProps } from './SpecifierNotes'

/**
 * The Note handler interface structure
 */
interface NoteHandler {
    /** handler when CKEditor is ready:
     * - sets the editor reference that will use to rollback changes when something went wrong
     */
    onReady: ClassicEditorOnReadyHandler
    /** handler for when the CKEditor loses focus:
     * - deactivates the editor when there are no changes to the content
     * - else, run optimistic mutation
     */
    onBlur: ClassicEditorHandler
    /** handler for when the CKEditor content changes with debounce:
     * - determines the current changes and sets the state accordingly
     */
    onChange: ClassicEditorHandler
}

export const useSpecifierNotesController = ({
    node,
    noteId,
    noteState,
    isArticleNoteSpecifier = false,
}: NoteProps) => {
    const [unsavedData, setUnsavedData] = noteState
    const formattedContentId = `specifier-note-${noteId || 'default'}`
    const notes =
        (isArticleNoteSpecifier ? node.parentComments : node.specifierNotes) ||
        []
    const note = notes[noteId] ?? ''

    // Prepare initial editor data and suggestions
    const initialData = useMemo(() => {
        const noteAsTextContent: Partial<TextContentElement> = {
            id: node.key,
            type: NodeTypes.Text,
            text: note ?? 'blank note',
        }
        return utils.elementToEditorData(noteAsTextContent)
    }, [node, note])

    const initialSuggestions = useMemo(() => note, [note])

    const hasInitialSuggestions = initialSuggestions.length > 0

    /** handles the activation and deactivation of the CKEditor */
    const { deactivate } = useCKEditorToggle(formattedContentId)

    /** hook that creates the mutation to update the content */
    const { mutateNoteAsync } = useContentMutationService()
    const { executeWithOptimistic, registerEditorRef } = useTransactionManager()

    const { setContentItemViewNodes } = useWorkspace2Actions()
    const { contentItemViewNodes } = useContentItemViewNode()

    const handleOnBlur = useCallback(() => {
        if (!unsavedData || (unsavedData === '' && !hasInitialSuggestions)) {
            // no changes, deactivate editor to conserve memory
            return deactivate()
        }

        let updatedNotes = [...notes]
        updatedNotes[noteId] = unsavedData

        // find and replace the notes in the node with the matching key
        const optimisticTreeViewNodes = replaceNodeNotesByKey(
            contentItemViewNodes,
            node.key,
            updatedNotes,
            isArticleNoteSpecifier
        )

        executeWithOptimistic({
            fallbackState: initialData,
            asyncAction: () =>
                mutateNoteAsync(node.key, updatedNotes, isArticleNoteSpecifier),
        })

        setContentItemViewNodes(optimisticTreeViewNodes)

        setUnsavedData('')
    }, [
        unsavedData,
        hasInitialSuggestions,
        node.key,
        noteId,
        mutateNoteAsync,
        deactivate,
        executeWithOptimistic,
        contentItemViewNodes,
        initialData,
        notes,
        isArticleNoteSpecifier,
    ])

    const debouncedOnChange = useMemo(
        () =>
            withDebounce<ClassicEditorHandler>(async (_, editor) => {
                if (editor?.getData()) {
                    const noteChanges = await editor.plugins
                        .get('TrackChangesData')
                        .getDataWithAcceptedSuggestions()

                    const changes = utils.getUnresolvedChanges(
                        noteChanges.toString()
                    )

                    setUnsavedData(changes)
                }
            }, 300),
        []
    )

    const handlers: NoteHandler = useMemo(
        () => ({
            onReady: (editor) => {
                // register the CKEditor instance to use in Transaction Manager (optimistic update)
                registerEditorRef(editor)
            },
            onBlur: handleOnBlur,
            onChange: debouncedOnChange,
        }),
        [handleOnBlur, debouncedOnChange, registerEditorRef]
    )

    return {
        note,
        handlers,
    }
}
